export default {
  name: "Temporary Need Information",
  prefix: "B",
  sections: [
    {
      fields: [
        {
          code: "B.1",
          machine_name: "b1_job_title",
          widget: "text",
          label: "Job Title",
          parse: ["noSpecialChars"],
          validate: [
            "required",
            {
              minLength: 5,
              message: "Job Title must be at least 5 characters",
            },
          ],
          maxLength: 60,
          tooltip: `
            Enter the title of the job opportunity for which the LCA is being sought.  The employer’s internal job title should be entered in this field. 
            <br />
            <strong><u>Note:</u></strong>  The job title must be the same for all nonimmigrant workers working on a single LCA.   The employer may file additional LCAs as needed.
            `,
        },
        {
          code: "B.2/B.3",
          machine_name: "b2_soc_code",
          widget: "soc",
          label: "SOC (ONET/OES) Code and Occupation Title",
          validate: ["required"],
          field_to_modify: "b3_soc_title",
          tooltip: `
            Enter the six-digit Standard Occupational Classification (SOC)/Occupational Network (O*NET) code for the occupation, or the occupational title associated with the SOC/O*NET code, which most clearly describes the work to be performed. For example, the six-digit SOC code for a Computer Systems Analyst is 15-1121.
            <br /> 
            <strong><u>Note:</u></strong> More information on SOC codes can be found at <u>http://www.bls.gov/soc/<u/>.
          `,
          disabled: true,
          disableWhenField: {
            array: true,
            when: "section_f",
            property: "f12_f13_f14_prevailing_wage_question",
            operator: "any",
            is: ["f13_is_oes_prevailing_wage", "f12_is_dol_prevailing_wage"],
          },
          warning: {
            text: "One or more Section F entries have been added using wages for this SOC code. In order to update the SOC code for this application, you must first remove those entries.",
            isVisible: {
              array: true,
              when: "section_f",
              property: "f12_f13_f14_prevailing_wage_question",
              operator: "any",
              is: ["f13_is_oes_prevailing_wage", "f12_is_dol_prevailing_wage"],
            },
          },
        },
        {
          code: "B.2/B.3",
          hidden: true,
          machine_name: "b3_soc_title", // Modified using field 'b2_soc_code'
          widget: "text",
          label: "SOC (ONET/OES) Code and Occupation Title",
          tooltip: `
            Enter the occupational title associated with the SOC/O*NET code.  For example, the occupational title   associated with SOC/O*NET code 15-1121 is Computer Systems Analyst.
            `,
        },
        {
          isGroup: {
            code: "B.4",
            machine_name: "b4_is_full_time",
            label: "Is this a full-time position?",
            required: true,
            tooltip: `
              Indicate whether the position is full-time by marking “Yes” or “No.” Although there is no regulatory definition for full-time employment for the H-1B, H-1B1, and E-3 programs, the Department generally considers 35 hours per week or more to be full-time. 
              <br />
              <strong><u>Note:</u></strong> If the position is part-time (less than 35 hours per week), the foreign worker(s) supported by the LCA must not regularly work more than the number of hours indicated (which may be a range of hours) on the United States Citizenship and Immigration Services (USCIS) Form(s) I-129 filed for the nonimmigrant(s).  All foreign worker(s) under the LCA must be part-time if Item B.4 is marked “No”; all nonimmigrant worker(s) must be full-time if Item B.4 is marked “Yes.” If the employer has both full-time and part-time nonimmigrant worker(s), then separate LCAs must be filed.
              `,
            fields: [
              {
                widget: "radio",
                choice: "Yes",
                value: "YES",
                validate: ["required"],
              },
              {
                widget: "radio",
                choice: "No",
                value: "NO",
                validate: ["required"],
              },
            ],
          },
        },
      ],
    },
    {
      fields: [
        {
          isGroup: {
            fieldLevelErrors: true,
            showIndividualFieldError: true,
            fields: [
              {
                code: "B.5",
                machine_name: "b5_begin_date",
                widget: "date",
                label: "Begin Date",
                maxDate: "b6_end_date",
                tooltip: `
                Enter the beginning date of the nonimmigrant worker’s (workers’) period of employment. The beginning date of employment cannot be more than 6 months from the date the LCA is submitted to the Department for processing. The beginning date of employment also cannot be prior to the date the LCA is submitted for processing. 
                `,
                validate: [
                  "required",
                  {
                    conditionalValueChecker: "b5_begin_date",
                    message: `
                    The end date for the worker's period of employment must be less than or equal to three years from the start date for H1B and H1B1 visa classes, and less than or equal to two years from the start date for the E3 visa class.
                    `,
                    needsFieldValues: true,
                    extraData: {
                      date: true,
                      typeCheck: "diff",
                      operator: ">",
                      valueToCompare: 1095,
                      begin_date: "b5_begin_date",
                      end_date: "b6_end_date",
                      condition: {
                        fieldToCheck: "a1_visa_type",
                        values: ["H-1B", "H-1B1 Singapore", "H-1B1 Chile"],
                      },
                    },
                  },
                  {
                    conditionalValueChecker: "b5_begin_date",
                    message: `
                    The end date for the worker's period of employment must be less than or equal to three years from the start date for H1B and H1B1 visa classes, and less than or equal to two years from the start date for the E3 visa class.
                    `,
                    needsFieldValues: true,
                    extraData: {
                      date: true,
                      typeCheck: "diff",
                      operator: ">",
                      valueToCompare: 1095,
                      begin_date: "b5_begin_date",
                      end_date: "b6_end_date",
                      condition: {
                        fieldToCheck: "a1_visa_type",
                        values: ["H-1B1 Singapore"],
                      },
                    },
                  },
                  {
                    conditionalValueChecker: "b5_begin_date",
                    message: `
                    The end date for the worker's period of employment must be less than or equal to three years from the start date for H1B and H1B1 visa classes, and less than or equal to two years from the start date for the E3 visa class.
                    `,
                    needsFieldValues: true,
                    extraData: {
                      date: true,
                      typeCheck: "diff",
                      operator: ">",
                      valueToCompare: 1095,
                      begin_date: "b5_begin_date",
                      end_date: "b6_end_date",
                      condition: {
                        fieldToCheck: "a1_visa_type",
                        values: ["H-1B1 Chile"],
                      },
                    },
                  },
                  {
                    conditionalValueChecker: "b5_begin_date",
                    message: `
                    The end date for the worker's period of employment must be less than or equal to three years from the start date for H1B and H1B1 visa classes, and less than or equal to two years from the start date for the E3 visa class.
                    `,
                    needsFieldValues: true,
                    extraData: {
                      date: true,
                      typeCheck: "diff",
                      operator: ">",
                      valueToCompare: 730,
                      begin_date: "b5_begin_date",
                      end_date: "b6_end_date",
                      condition: {
                        fieldToCheck: "a1_visa_type",
                        values: ["E-3 Australian"],
                      },
                    },
                  },
                  {
                    dateStartCheck: "b6_end_date",
                    message: `Please enter a begin date greater than today's date and within the next 183 days.`,
                    needsFieldValues: true,
                    extraData: {
                      greaterThanValue: 183,
                      lessThanValue: 0,
                    },
                  },
                  {
                    dateStartEnd: "b6_end_date",
                    message: "Begin Date must occur before End Date.",
                    extraData: {
                      type: "start",
                    },
                    needsFieldValues: true,
                  },
                ],
              },
              {
                code: "B.6",
                machine_name: "b6_end_date",
                widget: "date",
                label: "End Date",
                minDate: "b5_begin_date",
                tooltip: `
                Enter the end date for the nonimmigrant worker’s (workers’) period of employment. The end date of employment cannot be more than three (3) years after the start date for H-1B LCAs and initial H-1B1 LCAs. The end date employment for E-3 LCAs and H-1B1 extensions cannot be more than two (2) years after the start date. 
                `,
                validate: [
                  "required",
                  {
                    dateStartEnd: "b5_begin_date",
                    message: "End Date must occur after Begin Date.",
                    extraData: {
                      type: "end",
                    },
                    needsFieldValues: true,
                  },
                ],
              },
            ],
          },
        },
      ],
    },
    {
      fields: [
        {
          code: "B.7",
          machine_name: "b7_total_positions",
          widget: "text",
          label: "Total Worker Positions Being Requested for Certification",
          validate: [
            "required",
            "number",
            {
              minValue: 1,
              message: "Total Worker must be greater than 0",
            },
            {
              valueChecker: "b7_total_positions",
              message:
                "The sum of B.7a-B.7f must be equal to or greater than B.7 Total Worker Positions.",
              needsFieldValues: true,
              extraData: {
                valueOf: "b7_total_positions",
                fields: [
                  "b7a_new_employment",
                  "b7b_continuation",
                  "b7c_change_approved",
                  "b7d_new_concurrent",
                  "b7e_change_employer",
                  "b7f_amended_petition",
                ],
                check: "sumOfFields",
                operator: ">",
              },
            },
          ],
          parse: "positiveWholeNumber",
          maxLength: 4,
          tooltip: `
            This collection item contains two parts.
            <br />
            <strong>First,</strong> enter the total unique number of worker positions being requested for certification. This total cannot be “0” (zero). For this total, count each worker once.
            <br />
            <strong>Second,</strong>, use collection Items B.7(a) through (f) to enter the number of foreign workers in each applicable USCIS-defined category under which the employer plans to file various Form I-129s for the foreign workers. The total worker positions requested for certification must be less than or equal to the sum total of the numbers entered in collection Items (a) through (f). Every box MUST be filled and a single worker may fit into multiple boxes, as appropriate.
            <br />
            <strong><u>Note:</u></strong>  If the employer does not plan to request nonimmigrant worker(s) in a particular category in Items (a) through (f), please enter “0” (zero), as appropriate. If an individual nonimmigrant worker fits into more than one category, indicate that in Items (a) through (f).
            `,
        },
        {
          code: "B.7a-f",
          inputType: "hidden", // ==> hides only the input
          label:
            "Basis for the visa classification supported by this application <br/><i>(indicate total workers in each applicable category)</i>",
        },
        {
          code: "a",
          machine_name: "b7a_new_employment",
          widget: "text",
          label: "New Employment",
          validate: [
            "required",
            "number",
            {
              minValue: 0,
              message: "This field must be greater than or equal to zero",
            },
            {
              fieldComparison: "b7a_new_employment",
              needsFieldValues: true,
              extraData: {
                operator: "<=",
                valueOf: "b7_total_positions",
                valueOfFieldName: "Total Worker Positions",
              },
            },
          ],
          parse: "positiveWholeNumber",
          maxLength: 4,
        },
        {
          code: "b",
          machine_name: "b7b_continuation",
          widget: "text",
          label:
            "Continuation of previously approved employment without change with the same employer",
          validate: [
            "required",
            "number",
            {
              minValue: 0,
              message: "This field must be greater than or equal to zero",
            },
            {
              fieldComparison: "b7b_continuation",
              needsFieldValues: true,
              extraData: {
                operator: "<=",
                valueOf: "b7_total_positions",
                valueOfFieldName: "Total Worker Positions",
              },
            },
          ],
          parse: "positiveWholeNumber",
          maxLength: 4,
        },
        {
          code: "c",
          machine_name: "b7c_change_approved",
          widget: "text",
          label: "Change in previously approved employment",
          validate: [
            "required",
            "number",
            {
              minValue: 0,
              message: "This field must be greater than or equal to zero",
            },
            {
              fieldComparison: "b7c_change_approved",
              needsFieldValues: true,
              extraData: {
                operator: "<=",
                valueOf: "b7_total_positions",
                valueOfFieldName: "Total Worker Positions",
              },
            },
          ],
          parse: "positiveWholeNumber",
          maxLength: 4,
        },
        {
          code: "d",
          machine_name: "b7d_new_concurrent",
          widget: "text",
          label: "New concurrent employment",
          validate: [
            "required",
            "number",
            {
              minValue: 0,
              message: "This field must be greater than or equal to zero",
            },
            {
              fieldComparison: "b7d_new_concurrent",
              needsFieldValues: true,
              extraData: {
                operator: "<=",
                valueOf: "b7_total_positions",
                valueOfFieldName: "Total Worker Positions",
              },
            },
          ],
          parse: "positiveWholeNumber",
          maxLength: 4,
        },
        {
          code: "e",
          machine_name: "b7e_change_employer",
          widget: "text",
          label: "Change in employer",
          validate: [
            "required",
            "number",
            {
              minValue: 0,
              message: "This field must be greater than or equal to zero",
            },
            {
              fieldComparison: "b7e_change_employer",
              needsFieldValues: true,
              extraData: {
                operator: "<=",
                valueOf: "b7_total_positions",
                valueOfFieldName: "Total Worker Positions",
              },
            },
          ],
          parse: "positiveWholeNumber",
          maxLength: 4,
        },
        {
          code: "f",
          machine_name: "b7f_amended_petition",
          widget: "text",
          label: "Amended petition",
          validate: [
            "required",
            "number",
            {
              minValue: 0,
              message: "This field must be greater than or equal to zero",
            },
            {
              fieldComparison: "b7f_amended_petition",
              needsFieldValues: true,
              extraData: {
                operator: "<=",
                valueOf: "b7_total_positions",
                valueOfFieldName: "Total Worker Positions",
              },
            },
          ],
          parse: "positiveWholeNumber",
          maxLength: 4,
        },
      ],
    },
  ],
};
